export type TenantRouteId =
  | "customer-intake"
  | "customer-profiles"
  | "property-mapping"
  | "dispatch"
  | "finance"
  | "customer-to-cash"
  | "support"
  | "operations"
  | "sandbox-work"
  | "foundation"

export type TenantNavigationIcon = "customers" | "map" | "dispatch" | "finance" | "support" | "operations"

export interface TenantNavigationItem {
  readonly id: TenantRouteId
  readonly href: `#/${string}`
  readonly label: string
  readonly eyebrow: string
  readonly title: string
  readonly icon: TenantNavigationIcon
  readonly requiredCapabilities: readonly string[]
  readonly transportApis: readonly string[]
}

export const tenantNavigation = [
  {
    id: "customer-intake",
    href: "#/customers/new",
    label: "New customer",
    eyebrow: "Customer operations",
    title: "Create a customer",
    icon: "customers",
    requiredCapabilities: ["customer.create"],
    transportApis: ["previewInternalCustomerDuplicates", "createInternalCustomer"],
  },
  {
    id: "customer-profiles",
    href: "#/customers/type-profile",
    label: "Type profiles",
    eyebrow: "Customer operations",
    title: "Customer type profile",
    icon: "customers",
    requiredCapabilities: ["customer.type.manage"],
    transportApis: ["previewCustomerTypeChange", "applyCustomerTypeChange"],
  },
  {
    id: "property-mapping",
    href: "#/operations/property-mapping",
    label: "Property mapping",
    eyebrow: "Service locations",
    title: "Property measurement",
    icon: "map",
    requiredCapabilities: ["property_mapping.read"],
    transportApis: [
      "readPropertyMapConfiguration",
      "resolvePropertyAddress",
      "resolveRouteInputs",
    ],
  },
  {
    id: "dispatch",
    href: "#/operations/dispatch",
    label: "Dispatch",
    eyebrow: "Field operations",
    title: "Dispatch board",
    icon: "dispatch",
    requiredCapabilities: ["dispatch.read"],
    transportApis: [
      "readDispatchBoard",
      "startDispatchOptimization",
      "readDispatchOptimizationJob",
      "dispatchBoardAction",
      "dispatchDayOfChange",
    ],
  },
  {
    id: "finance",
    href: "#/finance",
    label: "Finance",
    eyebrow: "Finance and accounting",
    title: "Finance control center",
    icon: "finance",
    requiredCapabilities: ["finance.read"],
    transportApis: [
      "readFinancialReport",
      "readFinanceReferenceData",
      "dispatchFinanceCommand",
    ],
  },
  {
    id: "customer-to-cash",
    href: "#/finance/customer-to-cash",
    label: "Customer-to-cash",
    eyebrow: "Finance operations",
    title: "Customer-to-cash control",
    icon: "operations",
    requiredCapabilities: ["customer_to_cash.read"],
    transportApis: ["readCustomerToCashReport"],
  },
  {
    id: "support",
    href: "#/support",
    label: "Support",
    eyebrow: "Customer care",
    title: "Support cases",
    icon: "support",
    requiredCapabilities: ["support_case.read"],
    transportApis: ["readSupportCaseBoard"],
  },
  {
    id: "sandbox-work",
    href: "#/operations/sandbox-work",
    label: "Sandbox work",
    eyebrow: "Field operations",
    title: "Complete sandbox work",
    icon: "operations",
    requiredCapabilities: ["sandbox.customer_to_cash"],
    transportApis: ["runSandboxCustomerToCash"],
  },
  {
    id: "operations",
    href: "#/operations/overview",
    label: "Operations",
    eyebrow: "Tenant overview",
    title: "Operations dashboard",
    icon: "operations",
    requiredCapabilities: ["operations.read"],
    transportApis: ["readOperationsDashboard"],
  },
  {
    id: "foundation",
    href: "#/administration/foundation",
    label: "System trust",
    eyebrow: "Administration",
    title: "Authoritative trust path",
    icon: "support",
    requiredCapabilities: ["foundation.command"],
    transportApis: ["readFoundation", "mutateFoundation"],
  },
] as const satisfies readonly TenantNavigationItem[]

export const defaultTenantRoute = tenantNavigation[0]

export const tenantRouteFromHash = (hash: string): TenantNavigationItem =>
  tenantNavigation.find((item) => item.href === hash) ?? defaultTenantRoute
